import styled from 'styled-components'
import { useWindowSize } from 'usehooks-ts'
import countrydata from '../CountryTable/countrycomparedata.json'
import { useState } from 'react'
import { useLabels } from 'Hooks/useLabels'
import { labelStyles } from 'components/MainComponent/LabelStyles'
import { nfp } from 'components/MainComponent/textBuilder'

type CountryDataType = {
  name_de?: string
  name_fr?: string
  capita: number
  creme: number
}

const maxRowsOnClose = 10

export const CountryTable = ({
  userValue,
  lang
}: {
  userValue?: number
  lang: 'de' | 'fr' | undefined
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const Labels = useLabels(labelStyles)

  let { width } = useWindowSize()

  let drawData = [...(countrydata as CountryDataType[])].sort(
    (a, b) => b.creme - a.creme
  )

  function percent (value: number) {
    return (value / 11) * 100 + '%'
  }
  const userPos = drawData.findIndex(d => d.name_de === 'Sie') + 1
  return (
    <CountryTableRoot>
      <Labels chartTitleNm>
        {lang === 'de' ? (
          <>In diesen zehn Ländern wird am meisten Kaffee konsumiert</>
        ) : (
          <>En Suisse, on boit beaucoup de café en comparaison internationale</>
        )}
      </Labels>
      <br />
      <Labels subline>
        {lang === 'de' ? <>Konsum pro Kopf</> : <>Consommation par personne</>}
      </Labels>

      <Tail isOpen={isOpen} maxRows={maxRowsOnClose}>
        <Table>
          <Head>
            <tr>
              <td></td>
              {width > 550 && (
                <td>
                  <b>{lang === 'de' ? 'Kaffee' : 'Café'}</b>
                  <br />
                  {lang === 'de' ? 'pro Jahr in kg' : 'par année en kg'}
                </td>
              )}
              <td>
                <b>{lang === 'de' ? 'Tassen' : 'Tasses'}</b>
                <br />
                {lang === 'de' ? 'pro Tag' : 'par jour'}
              </td>{' '}
              <td></td>
            </tr>
          </Head>
          <Body>
            {drawData &&
              drawData.map((country, i) => {
                const { name_de, name_fr, capita, creme } = country
                const name = lang === 'de' ? name_de : name_fr
                if (!userValue && name_de === 'Sie') return null

                return (
                  <tr
                    key={'countryrow' + i}
                    className={name_de === 'Schweiz' ? 'swiss' : undefined}
                  >
                    <td className='pri'>
                      {i + 1}. {name}
                    </td>
                    {width > 550 && <td className='sec'>{nfp(capita)}</td>}
                    <td className='b'>{nfp(creme)}</td>{' '}
                    <td className='sec bar'>
                      <Bar
                        style={{
                          width: percent(creme),
                          backgroundColor: ' var(--table-bar-important)'
                        }}
                      />
                    </td>
                  </tr>
                )
              })}
          </Body>
        </Table>
      </Tail>
      <Source>
        {lang === 'de' ? 'Quelle:' : 'Source:'}{' '}
        <Link href='https://www.verenastreet.com/blogs/all-about-coffee/coffee-statistics?srsltid=AfmBOopzPuz1VUZyJcTy3ddJJmyiV5wcyUYT-f56QwRb4GxlFnXnN_Q3'>
          International Coffee Organization
        </Link>
      </Source>
    </CountryTableRoot>
  )
}

const Link=styled.a`
  color: var(--text-color) !important;
  text-decoration: none !important;
`

const Source = styled.div`
  font-size: 12px;
  color: var(--table-unimportant-row);
  line-height: 1.3em;
  vertical-align: baseline;
  margin-top: 10px;
`
const Bar = styled.div`
  background-color: var(--coffee-dark);
  display: inline-block;
  height: 14px;
`
const Button = styled.button`
  border: 0;
  padding: 10px;
  font-size: 15px;
  background-color: var(--more-button-bg);
  color: var(--text-color);
  &:hover {
    cursor: pointer;
    opacity: 0.7;
  }
`
const Tail = styled.div<{ isOpen: boolean; maxRows: number }>`
  max-height: ${({ isOpen, maxRows }) =>
    isOpen ? '2000px' : 55 + maxRows * 33 + 'px'};
  overflow: hidden;
  transition: max-height 0.25s ease;
  margin-bottom: 5px;
`
const CountryTableRoot = styled.div`
  font-family: var(--font-sans-serif);
  margin-top: 30px;
  margin-bottom: 60px;
  @media screen and (max-width: 711px) {
    width: calc(100vw - 40px);
  }
`

const Table = styled.table`
  table-layout: fixed;
  width: 100%;
  border-spacing: 0;

  td {
    box-sizing: border-box;

    padding-left: 10px;
    padding-right: 10px;
    text-align: right;
    &.pri {
      text-align: left;
      white-space: nowrap;
    }
    width: auto;
    &.bar {
      width: 25%;
    }
  }
  .swiss {
    td {
      background-color: var(--coffee-dark);
      font-weight: 700;
    }
  }
  .user {
    td {
      background-color: var(--orange-main);
      font-weight: 700;
      color: #fff;
      border-color: var(--orange-main);

      &.sec {
        color: #fff;
      }
      &.bar {
        text-align: left;
      }
    }
  }
`
const Head = styled.thead`
  font-size: 12px;
  color: var(--table-unimportant-row);
  line-height: 1.3em;
  vertical-align: baseline;
  td {
    b {
      color: var(--table-unimportant-row);

      font-weight: 700;
      font-size: 15px;
      line-height: 1.6em;
    }
    &.thi {
      text-align: left;
    }
    /* border-bottom: 1px solid var(--table-line-borders); */
    height: 50px;
  }
`
const Body = styled.thead`
  font-size: 15px;

  td {
    border-bottom: 1px solid var(--table-line-borders);
    line-height: 30px;
    min-height: 1px;
    text-align: right;

    &.pri {
      text-align: left;
    }
    &.sec {
      font-weight: 400;
      /* color: var(--brandblue-bright-3); */
    }
    &.bar {
      text-align: left;
    }

    &.b {
      font-weight: 700;
    }
  }
`
